import { ContentEnum } from 'component/page/detail/DetailPageModularize/detailPageModularize.types';
import { RootState } from 'store/store.types';
import {
    selectIntroductionFromContentDetailGenericSectionState,
    selectSimCardContent,
    selectResultFromDetailState,
    selectActivityItinerary,
    selectActivityLocationFromContentDetailState,
    selectSuggestedRouteFromGenericSectionState,
    selectPurchaseNoticeContent,
    selectReviewState,
    selectActivityDetail,
} from 'component/page/detail/store/detail.selectors';
import { isB } from 'core/window/bootstrapConfig';
import { selectExperimentsFromBootstrap, selectFeatureExperimentsFromBootstrap } from 'store/slice/bootstrap';
import { ExperimentId } from 'core/type/ExperimentId';
import ReviewSectionModular from 'component/page/detail/Review/ReviewSection/ReviewSectionModular';
import { VerticalContentsHandler } from 'component/page/detail/DetailPageModularize/ContentsHandler/VerticalContentsHandler';
import { ContentRegistryType, PACKAGE_OPTIONS_HREF, REVIEW_SECTION_HREF } from 'component/page/detail/DetailPageModularize/contentRegistry.type';
import DetailTitleSectionModular from 'component/page/detail/DetailCard/DetailTitleSectionModular';
import MosaicGallery from 'component/page/detail/DetailCard/ActivityCarousel/MosaicGallery';
import AtAGlanceSectionModular from 'component/page/detail/DetailCard/Overview/AtAGlanceSectionModular';
import ActivitiesHighlights from 'component/page/detail/Highlights/ActivitiesHighlights';
import AdditionalInformationSectionModular from 'component/page/detail/MoreInfoSections/AdditionalInformation/AdditionalInformationModular';
import ExploringActivitiesCarousel from 'component/page/detail/ExploringActivitiesCarousel/ExploringActivitiesCarousel';
import { CancellationPolicyModular } from 'component/page/detail/MoreInfoSections/CancellationPolicy/CancellationPolicyModular';
import { TabsContentsHandler } from 'component/page/detail/DetailPageModularize/ContentsHandler/TabsContentsHandler';
import { IntroductionSummaryModular } from 'component/page/detail/MoreInfoSections/IntroductionSummary/IntroductionSummaryModular';
import PackageOptionModular from 'component/page/detail/PackageOptions/PackageOptionsModular';
import Breadcrumb from 'component/page/detail/DetailCard/Breadcrumb/Breadcrumb';
import { selectScreenSize } from 'store/slice/screen';
import { mapActivityDetailByDetailResult, mapLogisticsByDetail } from 'gql/service/graph/activityService';
import { isDepartureAndReturnAvailable, isItineraryAvailable, isGeoLocationAvailable, mapAdditionalInfo } from 'component/page/detail/detailUtils';
import WhatToExpectModular from 'component/page/detail/MoreInfoSections/WhatToExpect/WhatToExpectModular';
import { SimCardSpecificationModular } from 'component/page/detail/MoreInfoSections/SimCardSpecification/SimCardSpecificationModular';
import DepartureAndReturn from 'component/page/detail/MoreInfoSections/DepartureAndReturn/DepartureAndReturnModular';
import LocationSectionModular from 'component/page/detail/MoreInfoSections/Location/LocationModular';
import AboutActivityModular from 'component/page/detail/MoreInfoSections/AboutActivity/AboutActivityModular';
import SuggestedRouteModular from 'component/page/detail/MoreInfoSections/SuggestedRoute/SuggestedRouteModular';
import FAQSectionModular from 'component/page/detail/FAQSection/FAQSectionModular';

export const ContentRegistry: ContentRegistryType = {
    [ContentEnum.VerticalContentHandler]: {
        createComponent: () => VerticalContentsHandler,
        titleCms: 0,
        elementName: '',
        href: '',
    },
    [ContentEnum.TabsContentHandler]: {
        createComponent: () => TabsContentsHandler,
        titleCms: 0,
        elementName: '',
        href: '',
    },
    [ContentEnum.Breadcrumb]: {
        createComponent: () => Breadcrumb,
        titleCms: 0,
        elementName: '',
        href: '',
        contentValidation: (state: RootState) => {
            const screenSize = selectScreenSize(state);
            return screenSize.desktop;
        },
    },
    [ContentEnum.TitleSection]: {
        createComponent: () => DetailTitleSectionModular,
        titleCms: 0,
        elementName: 'title-section',
        href: 'title-section',
    },
    [ContentEnum.MosaicGallery]: {
        createComponent: () => MosaicGallery,
        titleCms: 0,
        elementName: 'mosaic-gallery',
        href: 'mosaic-gallery',
    },
    [ContentEnum.PackageOptions]: {
        createComponent: () => PackageOptionModular,
        titleCms: 238726,
        elementName: 'activities-package-option',
        href: PACKAGE_OPTIONS_HREF,
    },
    [ContentEnum.ReviewSection]: {
        createComponent: () => ReviewSectionModular,
        titleCms: 102057,
        elementName: 'review-section-review-container',
        href: REVIEW_SECTION_HREF,
        isPrerenderHide: true,
        contentValidation: (state: RootState) => {
            const reviewState = selectReviewState(state);
            return !reviewState.isHideReview;
        },
    },
    [ContentEnum.AtAGlance]: {
        createComponent: () => AtAGlanceSectionModular,
        titleCms: 337252,
        elementName: 'at-a-glance',
        href: 'at-a-glance',
    },
    [ContentEnum.Highlight]: {
        createComponent: () => ActivitiesHighlights,
        titleCms: 351210,
        elementName: 'activities-highlights',
        href: 'activities-highlights',
    },
    [ContentEnum.ExploringActivitiesCarousel]: {
        createComponent: () => ExploringActivitiesCarousel,
        titleCms: 343921,
        elementName: 'exploring-activities-carousel',
        href: 'exploring-activities-carousel',
    },
    [ContentEnum.AdditionalInformation]: {
        createComponent: () => AdditionalInformationSectionModular,
        titleCms: 130768,
        elementName: 'activities-additional-info-container',
        href: 'additional-info-section',
        contentValidation: (state: RootState) => {
            const detailResult = selectResultFromDetailState(state);
            const purchaseNotice = selectPurchaseNoticeContent(state);
            const additionalInformation = mapAdditionalInfo(detailResult) || [];
            return additionalInformation.length + purchaseNotice.length > 0;
        },
    },
    [ContentEnum.CancellationPolicy]: {
        createComponent: () => CancellationPolicyModular,
        titleCms: 126854,
        elementName: 'activities-cancellation-container',
        href: 'cancellation-policy-section',
        isPrerenderHide: true,
    },
    [ContentEnum.IntroductionSummary]: {
        createComponent: () => IntroductionSummaryModular,
        titleCms: 0,
        elementName: 'introduction-summary',
        href: 'introduction-summary',
        contentValidation: (state: RootState) => {
            const introductionSummary = selectIntroductionFromContentDetailGenericSectionState(state);
            return !!introductionSummary?.title;
        },
        getTitle: (state: RootState) => {
            const introductionSummary = selectIntroductionFromContentDetailGenericSectionState(state);
            return introductionSummary?.title || null;
        },
    },
    [ContentEnum.SimCardSpec]: {
        createComponent: () => SimCardSpecificationModular,
        titleCms: 244232,
        elementName: 'sim-card-spec',
        href: 'sim-card-spec',
        contentValidation: (state: RootState) => {
            const simCardSpec = selectSimCardContent(state);
            return !!simCardSpec?.length;
        },
    },
    [ContentEnum.DepartureAndReturn]: {
        createComponent: () => DepartureAndReturn,
        titleCms: 126851,
        elementName: 'activities-departure-return-container',
        href: 'departure-and-return',
        contentValidation: (state: RootState) => {
            const detailResult = selectResultFromDetailState(state);
            const detail = mapActivityDetailByDetailResult(detailResult);
            const logistics = mapLogisticsByDetail(detail);
            const experiments = selectExperimentsFromBootstrap(state);
            return isDepartureAndReturnAvailable(logistics, isB(ExperimentId.ABE_4553, experiments));
        },
    },
    [ContentEnum.WhatToExpect]: {
        createComponent: () => WhatToExpectModular,
        titleCms: 126852,
        elementName: 'activities-what-to-expect-container',
        href: 'what-to-expect',
        contentValidation: (state: RootState) => {
            const itinerary = selectActivityItinerary(state);
            return isItineraryAvailable(itinerary);
        },
    },
    [ContentEnum.Location]: {
        createComponent: () => LocationSectionModular,
        titleCms: 20536,
        elementName: 'location',
        href: 'location',
        contentValidation: (state: RootState) => {
            const ActivityLocation = selectActivityLocationFromContentDetailState(state);
            const experiments = selectExperimentsFromBootstrap(state);
            return isB(ExperimentId.AFT_218, experiments) && isGeoLocationAvailable(ActivityLocation);
        },
    },
    [ContentEnum.SuggestedRoute]: {
        createComponent: () => SuggestedRouteModular,
        titleCms: 244231,
        elementName: 'suggested-route',
        href: 'suggested-route',
        contentValidation: (state: RootState) => {
            const suggestedRoute = selectSuggestedRouteFromGenericSectionState(state);
            return !!suggestedRoute;
        },
    },
    [ContentEnum.FAQs]: {
        createComponent: () => FAQSectionModular,
        titleCms: 276903,
        elementName: 'faqs',
        href: 'faqs',
        isPrerenderHide: true,
    },
    [ContentEnum.AboutActivity]: {
        createComponent: () => AboutActivityModular,
        titleCms: 337805,
        elementName: 'about-activity',
        href: 'about-activity',
        contentValidation: (state: RootState) => {
            const detail = selectActivityDetail(state);
            const featureExperiments = selectFeatureExperimentsFromBootstrap(state);
            return !!detail?.aboutActivity && featureExperiments.featureBehavior.ShowAboutActivityPanel;
        },
    },
};
